'use client';

import { type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useProfileQuery } from '@/lib/services/profile/hooks';
import { useLawFirmProfile } from '@/lib/services/account';
import {
  useStartTrial,
  useActivationSummary,
  type ActivationSummaryResponseDto
} from '@/lib/services/billing';
import { ActivationV2Landing } from '@/components/trial-activation/activation-v2-landing';
import { ActivationV2Success } from '@/components/trial-activation/activation-v2-success';
import { isDeactivated, isPendingUser } from '@/lib/utils/permissions';

const TRIAL_PARAM = 'trial';
const TRIAL_ACTIVATED_VALUE = 'activated';

/**
 * Membership statuses that mean the firm has CONVERTED after the Stripe
 * round-trip, so the gate may release: `trial` (the pending→trial path) or
 * `active`. The pre-conversion gated state (`pending`) is excluded — on return
 * from Stripe the gate polls until the backend webhook flips the firm to one of
 * these (LF-4535).
 */
const CONVERTED_STATUSES = ['trial', 'active'];

function isConverted(status?: string | null): boolean {
  return !!status && CONVERTED_STATUSES.includes(status);
}

/** How often to re-check the firm status while waiting for the Stripe webhook. */
const POLL_INTERVAL_MS = 2500;

function readReturningFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get(TRIAL_PARAM) ===
    TRIAL_ACTIVATED_VALUE
  );
}

function FullScreenLoader() {
  return (
    <div className="bg-surface-subtle flex min-h-screen items-center justify-center">
      <Loader2 className="text-foreground-subtle h-6 w-6 animate-spin" />
    </div>
  );
}

/**
 * Owns the start-trial mutation and renders the data-driven activation landing.
 * The CTA drives a Stripe checkout session and redirects on success; the backend
 * decides the trial window from the firm's own `customTrialDays` /
 * `trialExpiresAt` (the default free trial, or an immediate paid start when ops
 * has zeroed them).
 */
function ActivationV2({
  userEmail,
  summary
}: {
  userEmail: string;
  summary: ActivationSummaryResponseDto;
}) {
  const startTrial = useStartTrial();

  const handleActivate = () => {
    const origin = window.location.origin;
    startTrial.mutate(
      {
        successUrl: `${origin}/?${TRIAL_PARAM}=${TRIAL_ACTIVATED_VALUE}`,
        cancelUrl: `${origin}/`
      },
      {
        onSuccess: (data) => {
          window.location.href = data.checkoutUrl;
        }
      }
    );
  };

  return (
    <ActivationV2Landing
      userEmail={userEmail}
      summary={summary}
      state={startTrial.isPending ? 'redirecting' : 'idle'}
      onActivate={handleActivate}
      errorMessage={
        startTrial.isError
          ? 'Something went wrong starting your trial. Please try again.'
          : undefined
      }
    />
  );
}

/**
 * Activation gate (LF-4086) — the permanent, data-driven activation page for all
 * firms. It blocks a firm with the "Welcome to Vale" activation summary until a
 * payment method is added. It applies to exactly one firm shape: `pending` and
 * requiring the gate (`activationGateRequired`). Every other firm renders the
 * app untouched.
 *
 * There was a second arm — an `active-free` firm holding `trial-gate-opt-in` —
 * retired with its flag in VALE-1540 after three months in which no production
 * firm ever held it. It lost no capability: locking a free-plan firm out until
 * it pays is `pending` + `activationGateRequired` + `customTrialDays = 0`, which
 * reaches this same gate and converts straight to paid.
 *
 * On returning from Stripe (`?trial=activated`) the gate shows a temporary
 * success panel and polls the firm's `membershipStatus` until the backend
 * webhook flips it to `trial`/`active`, then hands to the app — restoring the
 * confirmation the v1 gate gave, which the v2 gate had dropped (LF-4535). The
 * poll also stops the gate re-firing: releasing to the app before the firm has
 * actually converted would re-show the gate.
 */
export function ActivationV2Gate({ children }: { children: ReactNode }) {
  const { data: profileData, isLoading: profileLoading } = useProfileQuery();

  const returning = readReturningFromUrl();
  const deactivated = isDeactivated(profileData?.user);

  // A deactivated user is already routed to the lock-out screen by the
  // dashboard layout — `children` IS that screen. The backend 403s
  // /account/profile unconditionally for these users, so skip the fetch
  // entirely rather than pay a network round trip for a request that can
  // never succeed. This is an optimisation, not the fix for VALE-825's
  // original retry-storm bug — that's fixed generally in `gatedAction()`
  // (VALE-954) — but it's kept because it renders this gate's lock-out
  // outcome immediately rather than after one request/response cycle.
  // While returning from Stripe, poll until the webhook converts the firm.
  const { profile: firm, isLoading: firmLoading } = useLawFirmProfile({
    enabled: !deactivated,
    refetchInterval: (query) =>
      returning && !isConverted(query.state.data?.membershipStatus)
        ? POLL_INTERVAL_MS
        : false
  });

  const membershipStatus = firm?.membershipStatus;
  const converted = isConverted(membershipStatus);

  // The gate reads a firm attribute, not a feature flag (VALE-873): the
  // exemption was held by 57 of 60 pending firms, which makes it an account
  // fact rather than a rollout switch. `?? true` mirrors the column's own
  // `NOT NULL DEFAULT true` for the deploy window in which this frontend is
  // live against a backend that has not yet run the migration — a missing value
  // means "the default", and the default has always been that a pending firm is
  // gated. The attribute is read for a `pending` firm only: every other status
  // carries it too (the column is NOT NULL for the whole table), so dropping the
  // status test would gate firms that have already paid.
  const gateApplies =
    membershipStatus === 'pending' && (firm?.activationGateRequired ?? true);

  // The summary backs the activation landing only; skip it on the return path.
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError
  } = useActivationSummary({ enabled: gateApplies && !returning });

  if (profileLoading || firmLoading) return <FullScreenLoader />;

  if (deactivated) return <>{children}</>;

  // Vale staff impersonating a user at this firm are not the audience for a commercial
  // prompt — they are here to configure the firm before it activates, and the gate keys on
  // the FIRM being pending, so it fires no matter which user they entered as (VALE-1231).
  //
  // Narrowed to a `pending` USER: the skip exists for the pre-activation setup window, so it
  // is scoped to a user who is themselves still in it. Staff impersonating an `active` or
  // `invited` user — the other two impersonatable statuses — now see exactly what that user
  // sees, which is the point of impersonating them. Note the two `pending`s are different
  // facts: the FIRM's `membershipStatus` is what the gate fires on, the USER's `status` is
  // what this skip reads.
  //
  // Session-scoped by construction: `impersonated` is resolved per request from the
  // server-side impersonation register, so it dies with the Keycloak session and the firm's
  // own `activationGateRequired` is untouched — unlike clearing that attribute in ops, which
  // is persistent and relies on somebody setting it back.
  //
  // Deliberately placed after the deactivated check (a lock-out still wins) and before the
  // Stripe return path, which a staff session is never on.
  if (profileData?.impersonated && isPendingUser(profileData?.user))
    return <>{children}</>;

  // Returning from Stripe — show the temporary success panel while the status
  // catches up (the poll above drives it), then hand to the app once converted.
  // Rendered after the load gate so the firm name is known on first paint.
  if (returning && !converted) {
    return (
      <ActivationV2Success
        firmName={firm?.name ?? profileData?.user?.lawFirm?.name ?? 'your firm'}
        userEmail={profileData?.user?.email ?? ''}
      />
    );
  }

  if (!gateApplies) return <>{children}</>;

  // Fail open if the summary can't be loaded — the gate is a FE-only commercial
  // prompt (the backend already grants a pending firm full access),
  // so a transient fetch error should let the user into the app, not trap them
  // behind a spinner.
  if (summaryError) return <>{children}</>;
  if (summaryLoading || !summary) return <FullScreenLoader />;

  const userEmail = profileData?.user?.email ?? '';
  return <ActivationV2 userEmail={userEmail} summary={summary} />;
}
